import {captureException} from '@sentry/react';
import {enqueueSnackbar} from 'notistack';
import {createSnapshotErrorReporting} from '@embertracking/ui/data/snapshotErrors';

import {auth} from '-/firebase';

// Both apps build with Vite into `assetsDir: 'static'`, so every code-split
// chunk is served from /static/<name>-<hash>.js. CRA's older
// /static/js/<name>.<hash>.chunk.js layout lives under /static/ too, so this
// covers a build still on that naming.
const isBuildAsset = url => typeof url === 'string' && /\/static\/.+\.js(\?|$)/.test(url);

/**
 * Detects if an error is a chunk load error (happens after deployments when cached chunks are stale)
 * @param {Error|Object} error - The error object
 * @param {string} filename - Optional filename from error event
 * @returns {boolean}
 */
export const isChunkLoadError = (error, filename = '') => {
    const errorMessage = error?.message || error?.toString() || '';

    // Standard chunk load error patterns (CRA/webpack)
    if (errorMessage.includes('Loading chunk') ||
        errorMessage.includes('ChunkLoadError')) {
        return true;
    }

    // Vite dynamic-import failures vary by browser:
    // - Chrome/Edge: "Failed to fetch dynamically imported module"
    // - Firefox: "error loading dynamically imported module"
    // - Safari (older): "Importing a module script failed"
    // - Safari 26+: "'text/html' is not a valid JavaScript MIME type."
    //   (fired when the SPA HTML fallback is served for a missing chunk)
    if (errorMessage.includes('dynamically imported module') ||
        errorMessage.includes('Importing a module script failed') ||
        errorMessage.includes('is not a valid JavaScript MIME type')) {
        return true;
    }

    // A build asset that won't parse is a broken chunk fetch, not an app bug:
    // - "Unexpected token '<'" — the SPA HTML fallback was served for a chunk
    //   that 404'd (stale bundle after a deploy).
    // - "Unexpected end of script" (Safari) / "Unexpected end of input"
    //   (Chrome/Firefox) — the response was truncated mid-body (#1537).
    //   Firebase Hosting omits Content-Length on compressed responses, so a
    //   connection that drops mid-chunk surfaces as a parse error rather than
    //   a network error.
    //
    // Matching the asset path is what keeps this from swallowing a genuine
    // SyntaxError in app code. We read sourceURL (Safari) and fileName
    // (Firefox) as well as window.onerror's filename, because React error
    // boundaries only ever hand us the error object.
    if (error?.name === 'SyntaxError' &&
        (errorMessage.includes('Unexpected token \'<\'') ||
         errorMessage.includes('Unexpected end of script') ||
         errorMessage.includes('Unexpected end of input')) &&
        (isBuildAsset(filename) || isBuildAsset(error?.sourceURL) || isBuildAsset(error?.fileName))) {
        return true;
    }

    return false;
};

// Recovering from a chunk load error means reloading the page, which only helps
// if the retry succeeds. Cap consecutive attempts so an asset that is
// persistently unparseable degrades to the ErrorBoundary's crash screen instead
// of looping. Attempts further apart than RELOAD_WINDOW_MS aren't consecutive —
// a real loop reloads within seconds, an unrelated failure an hour later should
// still get its own retry.
const RELOAD_GUARD_KEY = 'chunkReloadGuard';
const MAX_CONSECUTIVE_RELOADS = 2;
const RELOAD_WINDOW_MS = 10000;

const readReloadGuard = () => {
    try {
        const {count, at} = JSON.parse(sessionStorage.getItem(RELOAD_GUARD_KEY)) || {};
        return count && Date.now() - at <= RELOAD_WINDOW_MS ? count : 0;
    } catch (e) {
        // Blocked/absent sessionStorage, or a malformed value — start fresh.
        return 0;
    }
};

/**
 * Whether a chunk-load reload is still allowed. Peeks at the guard without
 * consuming an attempt, so the ErrorBoundary fallback can choose between the
 * "reloading" spinner and the crash screen.
 * @returns {boolean}
 */
export const canReloadForChunkError = () => readReloadGuard() < MAX_CONSECUTIVE_RELOADS;

/**
 * Reloads the page to recover from a chunk load error, unless we have already
 * retried too many times in a row.
 * @returns {boolean} true if a reload was triggered
 */
export const reloadForChunkError = () => {
    const count = readReloadGuard();

    if (count >= MAX_CONSECUTIVE_RELOADS) {
        console.error('Chunk load error persisted across reloads - showing the error screen');
        return false;
    }

    try {
        sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify({count: count + 1, at: Date.now()}));
    } catch (e) {
        // Without storage we can't count attempts, so the cap degrades to
        // "always allow" — the behaviour we had before the guard existed.
    }

    console.log('Chunk load error detected - reloading page to fetch latest version');
    window.location.reload();
    return true;
};

export const isOfflineError = error => {
    if (!error) return false;
    const message = error.message || error.toString?.() || '';
    const code = error.code || '';

    // Firestore: 'unavailable' or message about being offline
    if (code === 'unavailable' || code === 'failed-precondition') {
        if (message.includes('offline') || message.includes('backend')) return true;
    }
    if (message.includes('client is offline')) return true;

    // Axios: ERR_NETWORK / 'Network Error' / browser is offline
    if (code === 'ERR_NETWORK' || message === 'Network Error') return true;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;

    // Firebase Auth: background token refresh fails when the device drops the
    // network (mobile Safari backgrounding, Wi-Fi/cellular handoff). Surfaces as
    // an unhandled rejection with no app frames; navigator.onLine often still
    // reports true during a handoff so the check above misses it. (#784)
    if (code === 'auth/network-request-failed' || code === 'auth/timeout') return true;

    // Raw fetch() transport failure: the request never got a response (DNS,
    // connection refused, CORS block, ad/tracking blocker, offline). Always a
    // TypeError with a browser-specific message — never an app-logic bug, since
    // any HTTP response (even a 500) resolves the fetch. The Firebase SDKs use
    // fetch internally (e.g. Analytics fetchDynamicConfig hitting
    // firebase.googleapis.com, #1133 — commonly a tracking blocker), so these
    // surface via Sentry's fetch instrumentation with no app frames and
    // navigator.onLine often still true. Our own api calls go through axios
    // (ERR_NETWORK / 'Network Error', handled above), so this won't mask them.
    //   Chrome/Edge: 'Failed to fetch'
    //   Firefox:     'NetworkError when attempting to fetch resource.'
    //   Safari:      'Load failed'
    if (error.name === 'TypeError' &&
        (message === 'Failed to fetch' ||
         message.includes('NetworkError when attempting to fetch') ||
         message === 'Load failed')) {
        return true;
    }

    return false;
};

let lastOfflineNotice = 0;
const notifyOffline = () => {
    const now = Date.now();
    if (now - lastOfflineNotice < 10000) return;
    lastOfflineNotice = now;
    // Guarded: setupGlobalErrorHandlers runs before <SnackbarProvider> mounts,
    // and an early offline error here must never throw — that would re-enter
    // the global handler and loop. Notistack v3's module-level enqueueSnackbar
    // queues until the provider mounts, but we wrap defensively anyway.
    try {
        enqueueSnackbar('You appear to be offline. Some changes may not save until you reconnect.', {
            variant: 'warning',
            preventDuplicate: true,
            key: 'offline-notice'
        });
    } catch (e) {
        console.warn('Failed to show offline snackbar:', e);
    }
};

/**
 * Reports an error to Sentry
 * @param {Error|string} error - The error to report
 * @param {Object} context - Additional context about the error
 */
export const reportError = (error, context = {}) => {
    // Always log to console for development
    console.error('Error reported:', error, context);

    // Report to Sentry
    const errorObj = typeof error === 'string' ? new Error(error) : error;

    captureException(errorObj, {
        extra: context
    });
};

// Listener error callbacks: sign-out race suppression and the once-per-window
// wake-from-sleep resubscribe. Shared with the hub (#2050).
export const {snapshotErrorHandler, resilientSnapshot} = createSnapshotErrorReporting({auth, reportError});

/**
 * Setup global error handlers
 */
export const setupGlobalErrorHandlers = () => {
    // Handle uncaught errors
    window.addEventListener('error', event => {
        const errorMessage = event.error?.message || event.message || '';
        const isResizeObserverError = errorMessage.includes('ResizeObserver');

        if (isResizeObserverError) {
            // Ignore ResizeObserver errors
            console.log('Ignoring ResizeObserver error');
            event.preventDefault();
            return;
        }

        // Check for chunk load errors (happens after deployments when cached
        // chunks are stale). If the reload cap is hit the error falls through to
        // Sentry, so a persistently broken asset stays visible.
        if (isChunkLoadError(event.error, event.filename) && reloadForChunkError()) {
            return;
        }

        if (isOfflineError(event.error)) {
            notifyOffline();
            return;
        }

        // Report all other errors to Sentry
        reportError(event.error || event.message, {
            context: 'window.onerror',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            type: 'uncaught-error'
        });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', event => {
        const errorMessage = event.reason?.message || event.reason?.toString() || '';
        const isResizeObserverError = errorMessage.includes('ResizeObserver');

        if (isResizeObserverError) {
            // Ignore ResizeObserver errors
            console.log('Ignoring ResizeObserver error');
            event.preventDefault();
            return;
        }

        // Check for chunk load errors (happens after deployments when cached chunks are stale)
        if (isChunkLoadError(event.reason) && reloadForChunkError()) {
            return;
        }

        if (isOfflineError(event.reason)) {
            notifyOffline();
            event.preventDefault();
            return;
        }

        reportError(event.reason || 'Unhandled Promise Rejection', {
            context: 'unhandledrejection',
            promise: event.promise?.toString(),
            type: 'unhandled-rejection'
        });

        // Prevent default browser behavior (logging to console)
        // We're handling it ourselves
        event.preventDefault();
    });
};

/**
 * Wrapper for async functions that automatically reports errors
 * @param {Function} fn - The async function to wrap
 * @param {Object} context - Context to include in error reports
 * @returns {Function} Wrapped function
 */
export const withErrorReporting = (fn, context = {}) => {
    return async (...args) => {
        try {
            return await fn(...args);
        } catch (error) {
            reportError(error, {
                ...context,
                functionName: fn.name || 'anonymous'
            });
            throw error; // Re-throw so the caller can handle it
        }
    };
};
